import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HomeIcon from '@mui/icons-material/Home';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SellIcon from '@mui/icons-material/Sell';
import { Button, IconButton, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FunctionComponent } from 'react';

import { EventType, EventsQuery } from '../../apollo/__generated__/graphql';
import { Tooltip } from '../../components/Tooltip';
import { Layout } from '../../navigation/components/Layout/Layout';
import { CreateEventDrawer } from './components/CreateEventDrawer/CreateEventDrawer';
import { DeleteEventDialog } from './components/DeleteEventDialog/DeleteEventDialog';
import { EditEventDrawer } from './components/EditEventDrawer/EditEventDrawer';
import { MilestoneCell } from './components/MilestoneCell/MilestoneCell';
import { MilestonesDrawer } from './components/MilestonesDrawer/MilestonesDrawer';
import { VouchersDrawer } from './components/VouchersDrawer/VouchersDrawer';
import { useAdminEvents } from './useAdminEvents';

export const AdminEvents: FunctionComponent = () => {
  const {
    events,
    loading,
    onCreate,
    isCreateDrawerOpen,
    onCloseCreateDrawer,
    onShowMilestones,
    onShowVouchers,
    milestonesEventId,
    vouchersEventId,
    onCloseMilestonesDrawer,
    onCloseVouchersDrawer,
    onEditEvent,
    editingEvent,
    onCloseEditEvent,
    onDeleteEvent,
    eventToDeleteId,
    onShowDeleteDialog,
    onCloseDeleteDialog,
    onChangeArePaymentsEnabled,
    onChangeAreBedroomPaymentsEnabled,
    onChangeIsTargetEnabled,
    onChangeIsTinderEnabled,
  } = useAdminEvents();

  const columns: GridColDef<EventsQuery['events'][number]>[] = [
    {
      field: 'name',
      headerName: 'Nom',
      width: 120,
    },
    { field: 'description', headerName: 'Description' },
    {
      field: 'maxUsers',
      headerName: 'Participants',
      renderCell: (params) => {
        return (
          <Tooltip title="Payé/Validé/Maximum">
            <Typography variant="body2" sx={{ mt: 1.8 }}>
              {`${params.row.paidTicketUsersCount}/${params.row.validatedUsersCount}/${params.row.maxUsers}`}
            </Typography>
          </Tooltip>
        );
      },
    },
    { field: 'checkAddress', headerName: 'Adresse des chèques' },
    { field: 'startAt', headerName: 'Début', type: 'dateTime', width: 160 },
    {
      field: 'endAt',
      headerName: 'Fin',
      type: 'dateTime',
      width: 160,
    },
    {
      field: 'milestones',
      headerName: 'Paliers',
      type: 'custom',
      width: 200,
      renderCell: (params) => (
        <MilestoneCell milestones={params.row.milestones} />
      ),
    },
    {
      field: 'id',
      headerName: 'Actions',
      width: 380,
      renderCell: (params) => (
        <>
          <Tooltip title="Voir les paliers">
            <IconButton
              color="info"
              onClick={() => onShowMilestones(params.row.id)}
            >
              <FormatListBulletedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Voir les codes de réductions">
            <IconButton
              color="info"
              onClick={() => onShowVouchers(params.row.id)}
            >
              <SellIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Modifier l'événement">
            <IconButton color="info" onClick={() => onEditEvent(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              params.row.isTargetEnabled
                ? 'Désactiver les targets'
                : 'Activer les targets'
            }
          >
            <IconButton
              color={params.row.isTargetEnabled ? 'primary' : 'default'}
              onClick={() => {
                onChangeIsTargetEnabled(
                  params.row.id,
                  !params.row.isTargetEnabled,
                );
              }}
            >
              <CrisisAlertIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              params.row.isTinderEnabled
                ? 'Désactiver les meetics'
                : 'Activer les meetics'
            }
          >
            <IconButton
              color={params.row.isTinderEnabled ? 'primary' : 'default'}
              onClick={() => {
                onChangeIsTinderEnabled(
                  params.row.id,
                  !params.row.isTinderEnabled,
                );
              }}
            >
              <LocalFireDepartmentIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              params.row.arePaymentsEnabled
                ? 'Désactiver les paiements'
                : 'Réactiver les paiements'
            }
          >
            <IconButton
              color={params.row.arePaymentsEnabled ? 'error' : 'success'}
              onClick={() => {
                onChangeArePaymentsEnabled(
                  params.row.id,
                  !params.row.arePaymentsEnabled,
                );
              }}
            >
              {params.row.arePaymentsEnabled ? (
                <MoneyOffIcon />
              ) : (
                <AttachMoneyIcon />
              )}
            </IconButton>
          </Tooltip>
          {params.row.type === EventType.BedroomAndTent ? (
            <Tooltip
              title={
                params.row.areBedroomPaymentsEnabled
                  ? 'Désactiver les paiements en chambre'
                  : 'Réactiver les paiements en chambre'
              }
            >
              <IconButton
                color={
                  params.row.areBedroomPaymentsEnabled ? 'error' : 'success'
                }
                onClick={() => {
                  onChangeAreBedroomPaymentsEnabled(
                    params.row.id,
                    !params.row.areBedroomPaymentsEnabled,
                  );
                }}
              >
                <HomeIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Supprimer l'événement">
            <IconButton
              color="error"
              onClick={() => onShowDeleteDialog(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Layout title="Administration - Événements" activeTab="adminEvents">
      <Button onClick={onCreate} variant="outlined" sx={{ marginBottom: 1 }}>
        Créer un Événement
      </Button>
      <DataGrid
        rows={events}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        loading={loading}
      />
      <CreateEventDrawer
        isOpen={isCreateDrawerOpen}
        onClose={onCloseCreateDrawer}
      />
      <MilestonesDrawer
        eventId={milestonesEventId}
        onClose={onCloseMilestonesDrawer}
        isOpen={milestonesEventId !== null}
      />
      <VouchersDrawer
        eventId={vouchersEventId}
        onClose={onCloseVouchersDrawer}
        isOpen={vouchersEventId !== null}
      />
      {editingEvent ? (
        <EditEventDrawer
          event={editingEvent}
          isOpen={editingEvent !== null}
          onClose={onCloseEditEvent}
        />
      ) : null}
      <DeleteEventDialog
        onCancel={onCloseDeleteDialog}
        onDelete={onDeleteEvent}
        open={eventToDeleteId !== null}
      />
    </Layout>
  );
};
